import { DepartmentLabel } from 'model/DepartmentTerminology';
import { Group, Menu, Portal, Stack, Text, Tooltip } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconBackspace,
  IconBell,
  IconKeyOff,
  IconDotsVertical,
  IconMapPinPlus,
  IconPencil,
  IconServer,
  IconTableOptions,
  IconTrashX,
  IconUser,
} from '@tabler/icons-react';
import { useApi } from 'api/api-context';
import ActionIcon from 'components/ActionIcon';
import Button from 'components/Button';
import { useConfirm } from 'components/modals/message/MessageProvider';
import { usePasswordValidityModal } from 'components/modals/password-validity-modal/PasswordValidityModalProvider';
import { useResponsiveDesign } from 'hooks/use-responsive-design';
import { useTableProfilesModal } from 'components/modals/table-profiles-modal/TableProfilesModalProvider';
import P1Regular from 'components/typography/P1Regular';
import P2Regular from 'components/typography/P2Regular';
import { FEATURE_TOGGLE_ADMIN_IMPERSONATE_USER } from 'env';
import panic from 'errors/panic';
import { ROLE_ADMIN_ID } from 'model/Role';
import { useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { EDIT_USER_PAGE_PATH, NOTIFICATION_SETTINGS_PAGE_PATH, WELCOME_PAGE_PATH } from 'routes/paths';
import { CANNOT_SET_ENTITY_STATUS, ERROR_NOTIFICATION_COLOR } from 'utils/constants';

/**
 * Parameters of the UserActions component.
 */
export interface UserActionsProps {
  userId: number;
  fullName: string;
  status: boolean;
  compactOnTablet?: boolean;
  passwordValidUntil?: string;
  managesDevices: { deviceId: number; deviceName: string }[];
  departmentsContact: { departmentId: number; departmentName: string }[];
  onStatusChange: (status: boolean) => void;
  onPasswordValidityChange?: (passwordValidUntil: string) => void;
}

/**
 * Displays actions for a user in a table.
 */
export default function UserActions({
  userId,
  fullName,
  status,
  onStatusChange,
  compactOnTablet = false,
  passwordValidUntil,
  managesDevices,
  departmentsContact,
  onPasswordValidityChange,
}: UserActionsProps) {
  const navigate = useNavigate();
  const { getAction, setImpersonate, roleId, userId: contextUserId } = useApi();
  const { confirm } = useConfirm();
  const { isMobile, tableActionMode } = useResponsiveDesign();
  const isCompact = compactOnTablet && tableActionMode !== 'desktop';
  const menuOnly = compactOnTablet && tableActionMode === 'mobile';
  const { openTableProfilesModal } = useTableProfilesModal();
  const { openPasswordValidityModal } = usePasswordValidityModal();

  const discardUserMessage = useMemo(
    () => (
      <Stack spacing={16}>
        <P1Regular c="gray.8">Naozaj chcete vyradiť používateľa?</P1Regular>
        <P1Regular c="gray.8">
          <DepartmentLabel
            objectLabel="Používateľ bude odstránený zo všetkých organizácií a položiek úrovne."
            departmentLabel="Používateľ bude odstránený zo všetkých organizácií a stredísk."
          />
        </P1Regular>
        {managesDevices.length > 0 && (
          <Stack spacing={4}>
            <P1Regular c="gray.8">Používateľ bude odstránený ako zodpovedná osoba pri týchto zariadeniach:</P1Regular>
            {managesDevices.map(({ deviceId, deviceName }) => (
              <Group key={deviceId} spacing={8} pl={8}>
                <IconServer size={16} />
                <P2Regular>{deviceName}</P2Regular>
              </Group>
            ))}
          </Stack>
        )}
        {departmentsContact.length > 0 && (
          <Stack spacing={4}>
            <P1Regular c="gray.8">
              <DepartmentLabel
                objectLabel="Používateľ bude odstránený ako kontaktná osoba v týchto položkách úrovne:"
                departmentLabel="Používateľ bude odstránený ako kontaktná osoba na týchto strediskách:"
              />
            </P1Regular>
            {departmentsContact.map(({ departmentId, departmentName }) => (
              <Group key={departmentId} spacing={8} pl={8}>
                <IconMapPinPlus size={16} />
                <P2Regular>{departmentName}</P2Regular>
              </Group>
            ))}
          </Stack>
        )}
      </Stack>
    ),
    [managesDevices, departmentsContact]
  );

  /**
   * Makes a call to the API to set the status of the user.
   */
  const setStatusRemote = useCallback(
    (status: boolean) => {
      const userSetStatus = getAction('UserSetStatus');

      userSetStatus({ parameters: { userId: String(userId) }, payload: { status } })
        .then(() => onStatusChange(status))
        .catch((error) => {
          if (error.response.data.error.code === CANNOT_SET_ENTITY_STATUS) {
            notifications.show({
              title: 'Chyba!',
              message: 'Tohto používateľa nemôžete deaktivovať.',
              color: ERROR_NOTIFICATION_COLOR,
            });
          } else {
            panic(error);
          }
        });
    },
    [getAction, userId]
  );

  /**
   * Confirms the status change.
   */
  const confirmSetStatus = useCallback(
    (status: boolean) => {
      const title = status ? 'Zaradiť používateľa' : 'Vyradiť používateľa';
      const message = status ? 'Naozaj chcete zaradiť používateľa?' : discardUserMessage;

      confirm({
        title,
        content: message,
        onConfirm: () => setStatusRemote(status),
      });
    },
    [confirm, setStatusRemote, discardUserMessage]
  );

  /**
   * Confirms the impersonation.
   */
  const confirmImpersonate = useCallback(() => {
    if (roleId !== ROLE_ADMIN_ID) {
      /* istanbul ignore next -- explicitly authorized unreachable coverage blocker */
      return; // Only admins can impersonate.
    }

    confirm({
      title: `Vystupovať ako ${fullName}`,
      content: 'Naozaj chcete vystupovať ako tento používateľ?',
      onConfirm: () => {
        const userGetMeAction = getAction('AuthLoggedUserInfo');

        userGetMeAction({ headers: { 'X-Impersonate': userId } })
          .then((user) => {
            setImpersonate(user);
            navigate(WELCOME_PAGE_PATH.original);
          })
          .catch(panic);
      },
    });
  }, [navigate, confirm, getAction, setImpersonate, fullName, userId, roleId]);

  const openSetPasswordValidityModal = useCallback(() => {
    if (roleId !== ROLE_ADMIN_ID || contextUserId === userId) {
      /* istanbul ignore next -- explicitly authorized unreachable coverage blocker */
      return;
    }

    openPasswordValidityModal({
      userId,
      fullName,
      passwordValidUntil,
      onPasswordValidityChange,
    });
  }, [
    contextUserId,
    fullName,
    onPasswordValidityChange,
    openPasswordValidityModal,
    passwordValidUntil,
    roleId,
    userId,
  ]);

  return (
    <Group spacing={12} noWrap>
      {!menuOnly &&
        (isCompact ? (
          contextUserId !== userId ? (
            <Tooltip label="Editovať" events={{ hover: true, focus: true, touch: true }}>
              <ActionIcon
                variant="secondary"
                size="md"
                component={Link}
                to={EDIT_USER_PAGE_PATH.insert({ userId })}
                disabled={!status}
              >
                <IconPencil />
              </ActionIcon>
            </Tooltip>
          ) : (
            <ActionIcon variant="secondary" size="md" disabled>
              <IconPencil />
            </ActionIcon>
          )
        ) : contextUserId !== userId ? (
          <Button
            size="md"
            component={Link}
            to={EDIT_USER_PAGE_PATH.insert({ userId })}
            variant="secondary"
            leftIcon={<IconPencil />}
            disabled={!status}
          >
            Editovať
          </Button>
        ) : (
          // TODO maybe this does not have to be here but when there's no button, the menu is not aligned properly
          <Button size="md" variant="tertiary" w={115}>
            To ste vy
          </Button>
        ))}
      <Menu position="bottom-end">
        <Menu.Target>
          <ActionIcon variant="tertiary" size={isMobile ? 'sm' : 'md'}>
            <IconDotsVertical stroke="1.5" />
          </ActionIcon>
        </Menu.Target>
        <Portal>
          <Menu.Dropdown>
            <Menu.Label>
              <Text maw={160} truncate>
                {fullName}
              </Text>
            </Menu.Label>
            {status && (
              <>
                {menuOnly && contextUserId !== userId && (
                  <Menu.Item
                    component={Link}
                    to={EDIT_USER_PAGE_PATH.insert({ userId })}
                    icon={<IconPencil stroke="1.5" size={20} />}
                  >
                    Editovať
                  </Menu.Item>
                )}
                {FEATURE_TOGGLE_ADMIN_IMPERSONATE_USER && roleId === ROLE_ADMIN_ID && contextUserId !== userId && (
                  <Menu.Item onClick={confirmImpersonate} icon={<IconUser stroke="1.5" size={20} />}>
                    Vystupovať ako
                  </Menu.Item>
                )}
                <Menu.Item
                  component={Link}
                  to={NOTIFICATION_SETTINGS_PAGE_PATH.insert({ userId })}
                  icon={<IconBell stroke="1.5" size={20} />}
                >
                  Nastavenia upozornení
                </Menu.Item>
                <Menu.Item
                  onClick={() => openTableProfilesModal({ userId })}
                  icon={<IconTableOptions stroke="1.5" size={20} />}
                >
                  Tabuľkové profily
                </Menu.Item>
                {roleId === ROLE_ADMIN_ID && contextUserId !== userId && (
                  <Menu.Item onClick={openSetPasswordValidityModal} icon={<IconKeyOff stroke="1.5" size={20} />}>
                    Nastaviť platnosť hesla
                  </Menu.Item>
                )}
                <Menu.Divider />
              </>
            )}
            {contextUserId !== userId && (
              <Menu.Item
                color={status ? 'red.8' : 'red.8'}
                onClick={() => confirmSetStatus(!status)}
                icon={status ? <IconTrashX stroke={1.5} size={20} /> : <IconBackspace stroke={1.5} size={20} />}
              >
                {status ? 'Vyradiť' : 'Zaradiť'}
              </Menu.Item>
            )}
          </Menu.Dropdown>
        </Portal>
      </Menu>
    </Group>
  );
}
